import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { formatDate, formatDateTime, isGameMaster, getInitials, getAvatarColor } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Calendar, 
  Clock,
  MapPin,
  ArrowLeft,
  Save,
  Send,
  Loader2,
  Eye,
  EyeOff,
  Check,
  X,
  HelpCircle
} from 'lucide-react';
import { LoreLinksPanel } from '../components/lore/LoreLinksPanel';
import { MentionTextarea } from '../components/mentions/MentionTextarea';
import { MentionText } from '../components/mentions/MentionText';
import { syncEntityMentions } from '../lib/mentions';

const API_URL = '/api';

export default function SessionDetailPage() {
  const { campaignId, sessionId } = useParams();
  const navigate = useNavigate();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  useEffect(() => {
    fetchSession();
  }, [sessionId]);

  const fetchSession = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/sessions/${sessionId}`);
      setSession(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error(t('error_generic', language));
      navigate(`/campaigns/${campaignId}/sessions`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.patch(`${API_URL}/sessions/${sessionId}`, {
        title: editData.title,
        narrative_summary: editData.narrative_summary,
        key_events: editData.key_events,
        player_decisions: editData.player_decisions,
        published_summary: editData.published_summary,
        mj_prep_notes: editData.mj_prep_notes
      });
      setSession(response.data);
      setEditMode(false);
      toast.success(t('success_saved', language));
      syncEntityMentions({
        campaignId,
        sourceType: 'session',
        sourceId: sessionId,
        sourceFields: {
          narrative_summary: editData.narrative_summary || '',
          key_events: (editData.key_events || []).join('\n'),
          player_decisions: (editData.player_decisions || []).join('\n'),
          mj_prep_notes: editData.mj_prep_notes || ''
        }
      }).catch(() => {});
    } catch (error) {
      toast.error(t('error_generic', language));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await axios.post(`${API_URL}/sessions/${sessionId}/publish`);
      setSession(prev => ({ ...prev, is_published: true }));
      toast.success(t('success_published', language));
    } catch (error) {
      toast.error(t('error_generic', language));
    } finally {
      setPublishing(false);
    }
  };

  const handleAttendance = async (userId, status) => {
    try {
      await axios.post(`${API_URL}/sessions/${sessionId}/attendance`, {
        user_id: userId,
        status: status
      });
      setSession(prev => ({
        ...prev,
        attendance: prev.attendance.map(a => 
          a.user_id === userId ? { ...a, status } : a
        )
      }));
      toast.success(t('success_saved', language));
    } catch (error) {
      toast.error(t('error_generic', language));
    }
  };

  if (loading || !session) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 animate-fade-in" data-testid="session-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(`/campaigns/${campaignId}/sessions`)}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('back', language)}
        </Button>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-cinzel text-3xl font-bold">{session.title}</h1>
              {session.is_published ? (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-success/10 text-success">
                  <Eye className="h-3 w-3" />
                  {t('session_published', language)}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                  <EyeOff className="h-3 w-3" />
                  {t('session_draft', language)}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDateTime(session.scheduled_date, language === 'fr' ? 'fr-FR' : 'en-US')}
              </span>
              {session.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {session.location}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {Math.floor(session.estimated_duration / 60)}h{session.estimated_duration % 60 > 0 ? `${session.estimated_duration % 60}m` : ''}
              </span>
            </div>
          </div>
          
          {isMJ && (
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <Button variant="outline" onClick={() => { setEditMode(false); setEditData(session); }}>
                    {t('cancel', language)}
                  </Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    {t('save', language)}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setEditMode(true)} data-testid="edit-btn">
                    {t('edit', language)}
                  </Button>
                  {!session.is_published && (
                    <Button onClick={handlePublish} disabled={publishing} className="fantasy-button" data-testid="publish-btn">
                      {publishing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                      {t('session_publish', language)}
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <Tabs defaultValue="summary" className="space-y-6">
          <TabsList>
            <TabsTrigger value="summary" data-testid="tab-summary">
              {language === 'fr' ? 'Résumé' : 'Summary'}
            </TabsTrigger>
            <TabsTrigger value="attendance" data-testid="tab-attendance">
              {t('session_attendance', language)}
            </TabsTrigger>
            {isMJ && (
              <TabsTrigger value="prep" data-testid="tab-prep">
                {language === 'fr' ? 'Préparation MJ' : 'GM Prep'}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>{t('session_summary', language)}</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <MentionTextarea
                    campaignId={campaignId}
                    language={language}
                    value={editData.narrative_summary || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, narrative_summary: e.target.value }))}
                    placeholder={language === 'fr' ? 'Résumé narratif de la session... (tapez @ pour mentionner)' : 'Narrative summary of the session... (type @ to mention)'}
                    rows={8}
                    data-testid="summary-input"
                  />
                ) : (
                  <MentionText
                    text={session.narrative_summary || (language === 'fr' ? 'Aucun résumé pour le moment.' : 'No summary yet.')}
                    campaignId={campaignId}
                    language={language}
                  />
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>{t('session_key_events', language)}</CardTitle>
                </CardHeader>
                <CardContent>
                  {editMode ? (
                    <MentionTextarea
                      campaignId={campaignId}
                      language={language}
                      value={(editData.key_events || []).join('\n')}
                      onChange={(e) => setEditData(prev => ({
                        ...prev,
                        key_events: e.target.value.split('\n').filter(l => l.trim())
                      }))}
                      placeholder={language === 'fr' ? 'Un événement par ligne... (@ pour mentionner)' : 'One event per line... (@ to mention)'}
                      rows={6}
                      data-testid="events-input"
                    />
                  ) : (
                    <ul className="space-y-2">
                      {session.key_events?.length > 0 ? session.key_events.map((event, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          <MentionText text={event} campaignId={campaignId} language={language} />
                        </li>
                      )) : (
                        <p className="text-muted-foreground">
                          {language === 'fr' ? 'Aucun événement enregistré.' : 'No events recorded.'}
                        </p>
                      )}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>{t('session_decisions', language)}</CardTitle>
                </CardHeader>
                <CardContent>
                  {editMode ? (
                    <MentionTextarea
                      campaignId={campaignId}
                      language={language}
                      value={(editData.player_decisions || []).join('\n')}
                      onChange={(e) => setEditData(prev => ({
                        ...prev,
                        player_decisions: e.target.value.split('\n').filter(l => l.trim())
                      }))}
                      placeholder={language === 'fr' ? 'Une décision par ligne... (@ pour mentionner)' : 'One decision per line... (@ to mention)'}
                      rows={6}
                      data-testid="decisions-input"
                    />
                  ) : (
                    <ul className="space-y-2">
                      {session.player_decisions?.length > 0 ? session.player_decisions.map((decision, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-warning mt-1">•</span>
                          <MentionText text={decision} campaignId={campaignId} language={language} />
                        </li>
                      )) : (
                        <p className="text-muted-foreground">
                          {language === 'fr' ? 'Aucune décision enregistrée.' : 'No decisions recorded.'}
                        </p>
                      )}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>{t('session_attendance', language)}</CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Gérez la présence des joueurs pour cette session'
                    : 'Manage player attendance for this session'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {session.attendance?.map((att) => {
                    const canEdit = isMJ || att.user_id === user?.id;
                    return (
                      <div key={att.user_id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30" data-testid={`attendance-${att.user_id}`}>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className={getAvatarColor(att.user_id)}>
                              {getInitials(att.username)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{att.username}</span>
                        </div>
                        
                        {canEdit ? (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant={att.status === 'confirmed' ? 'default' : 'outline'}
                              onClick={() => handleAttendance(att.user_id, 'confirmed')}
                              className={att.status === 'confirmed' ? 'bg-success hover:bg-success/90' : ''}
                              data-testid={`confirm-${att.user_id}`}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant={att.status === 'absent' ? 'default' : 'outline'}
                              onClick={() => handleAttendance(att.user_id, 'absent')}
                              className={att.status === 'absent' ? 'bg-destructive hover:bg-destructive/90' : ''}
                              data-testid={`absent-${att.user_id}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className={`text-sm px-3 py-1 rounded-full ${
                            att.status === 'confirmed' ? 'bg-success/10 text-success' :
                            att.status === 'absent' ? 'bg-destructive/10 text-destructive' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {t(`session_${att.status}`, language)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Prep Tab (MJ only) */}
          {isMJ && (
            <TabsContent value="prep">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>{t('session_prep', language)}</CardTitle>
                  <CardDescription>
                    {language === 'fr' 
                      ? 'Notes privées de préparation (non visibles par les joueurs)'
                      : 'Private preparation notes (not visible to players)'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {editMode ? (
                    <MentionTextarea
                      campaignId={campaignId}
                      language={language}
                      value={editData.mj_prep_notes || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, mj_prep_notes: e.target.value }))}
                      placeholder={language === 'fr' ? 'Notes de préparation... (@ pour mentionner)' : 'Preparation notes... (@ to mention)'}
                      rows={12}
                      data-testid="prep-input"
                    />
                  ) : (
                    <MentionText
                      text={session.mj_prep_notes || (language === 'fr' ? 'Aucune note de préparation.' : 'No preparation notes.')}
                      campaignId={campaignId}
                      language={language}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Lore Links Panel */}
        <LoreLinksPanel
          entityType="session"
          entityId={sessionId}
          entityName={session.title}
          campaignId={campaignId}
          isMJ={isMJ}
          data-testid="lore-links-panel"
        />
      </div>
    </MainLayout>
  );
}

